import { isCarrotAdmin } from "@/Utils/authUtils";
import { COORDINATES_REGEX } from "@/Utils/isValidCoordinatesValue";
import { EMAIL_REGEX } from "@/Utils/isValidEmail";
import { NUMBER_REGEX } from "@/Utils/isValidNumber";
import { RealEstateCategory, VALID_BUILDING_CATEGORIES } from "@/api/types";
import FormInput from "@/components/Form/FormInput";
import SearchInput from "@/components/Form/SearchInput";
import { IInputOption } from "@/components/GenericComponents/input";
import { getRealEstateTabs } from "@/pages/configuration";
import { Translate, useTrans } from "@/translations";

import { FC, useMemo } from "react";
import { UseFormReturn } from "react-hook-form";

export type BuildingFormData = {
  name: string;
  category: RealEstateCategory;
  contactEmail: string;
  area: string;
  street?: string;
  postalCode?: string;
  postalRegion?: string;
  country?: string;
  coordinates?: string;
};

interface TerminalFormProps {
  formHandler: UseFormReturn<BuildingFormData>;
}

export const BuildingForm: FC<TerminalFormProps> = ({ formHandler }) => {
  const { t } = useTrans();
  const {
    register,
    control,
    setValue,
    formState: { errors },
  } = formHandler;

  const buildingCategoryInputOptions: IInputOption[] = useMemo(
    () =>
      VALID_BUILDING_CATEGORIES.map((key) => ({
        option: `entities:${key}`,
        value: key,
        translate: true,
      })),
    [],
  );

  const actionReportSettingsTabIndex = useMemo(
    () =>
      getRealEstateTabs(isCarrotAdmin()).findIndex(
        (tab) => tab.title === "configLabels:actionReport",
      ),
    [],
  );

  return (
    <form>
      <div className="mb-6">
        <FormInput
          name="name"
          label={t("importLabels:terminalName")}
          register={register}
          error={errors?.name}
          required
          showErrorInTooltip={false}
        />
      </div>
      <div className="mb-6">
        <SearchInput
          label={t("formLabels:buildingCategory")}
          placeholder={t("actions:selectProperty", {
            property: t("formLabels:category").toLowerCase(),
          })}
          required
          name="category"
          data={buildingCategoryInputOptions}
          initialValue={formHandler.getValues("category")}
          noInitialOption={!formHandler.getValues("category")}
          control={control}
          setValue={setValue}
          rules={{ required: true, minLength: 1 }}
          errorObj={errors.category}
          register={register}
        />
      </div>
      <div className="mb-6">
        <FormInput
          name="contactEmail"
          label={t("formLabels:contactEmail")}
          register={register}
          error={errors?.contactEmail}
          regexPattern={EMAIL_REGEX}
          required
          patternError="errors:mustBeValidEmail"
          showErrorInTooltip={false}
        />
        <div className="text-grey6 mt-2 text-sm">
          <Translate
            i18nKey="hints:buildingContactEmail"
            components={[
              // eslint-disable-next-line jsx-a11y/anchor-has-content
              <a
                href={`/settings?tab=${actionReportSettingsTabIndex}`}
                className="text-grey6 underline"
              />,
            ]}
          />
        </div>
      </div>
      <div className="mb-6">
        <FormInput
          name="area"
          label={t("formLabels:area")}
          register={register}
          error={errors?.area}
          regexPattern={NUMBER_REGEX}
          patternError="errors:mustBeValidNumber"
          showErrorInTooltip={false}
        />
      </div>
      <div className="mb-6">
        <FormInput
          name="street"
          label={t("importLabels:terminalAddressStreet")}
          register={register}
          error={errors?.street}
          showErrorInTooltip={false}
        />
      </div>
      <div className="mb-6">
        <FormInput
          name="postalRegion"
          label={t("importLabels:terminalAddressCity")}
          register={register}
          error={errors?.postalRegion}
          showErrorInTooltip={false}
        />
      </div>
      <div className="mb-6">
        <FormInput
          name="postalCode"
          label={t("importLabels:terminalAddressPostalCode")}
          register={register}
          error={errors?.postalCode}
          showErrorInTooltip={false}
        />
      </div>
      <div className="mb-6">
        <FormInput
          name="country"
          label={t("importLabels:terminalAddressCountry")}
          register={register}
          error={errors?.country}
          showErrorInTooltip={false}
        />
      </div>
      <div className="mb-6">
        <FormInput
          name="coordinates"
          label={t("formLabels:coordinates")}
          register={register}
          error={errors?.coordinates}
          regexPattern={COORDINATES_REGEX}
          patternError="errors:mustBeValidCoordinates"
        />
      </div>
    </form>
  );
};
